import { useState, type CSSProperties } from "react";
import { ChevronRight } from "./icons";
import { AgentKindLogo, kindSpec } from "../../agents";
import {
  formatCompact,
  formatPct,
  formatUSD,
  savedPct,
  UNTAGGED_AGENT,
  type AgentRollup,
} from "../../analytics";

/* Rows shown before the list collapses behind "Show all". */
const COLLAPSED_ROWS = 3;

/* Description shown on the synthetic `(untagged)` row in place of a registry
 * one — it has no registry entry to describe it. Same explanation the failure
 * drawer's untagged bucket carries. */
const UNTAGGED_NOTE =
  "Requests the gateway received with no x-agent-id, so they can't be attributed to an agent.";

/* Rows visible before the expanded list scrolls. Each row is ~61px tall (33px
 * logo + 13px vertical padding + border) with an 8px gap, so the scroll
 * viewport is capped at VISIBLE_ROWS × (61 + 8)px. */
const VISIBLE_ROWS = 6;
const ROW_PX = 69;

/* Shared column template so the bordered rows align as one table:
 * logo | name+id | type | description | spend | tokens | saved | insights | → */
const ROW_GRID =
  "grid grid-cols-[33px_minmax(150px,1.1fr)_minmax(80px,0.6fr)_minmax(0,1.8fr)_72px_72px_120px_minmax(0,140px)_15px] items-center gap-4";

/* Connected agents — the Figma overview's row list (node 284:461): a plain
 * section header ("Your connected agents (n)") over individually bordered rounded
 * rows. Collapsed to the top COLLAPSED_ROWS; "Show all" (centred under the
 * list) opens the full
 * (scrollable) list. Row click drills into that agent. */
export function ConnectedAgentsCard({
  agents,
  descriptions,
  recentlyUsed,
  recommendations,
  onSelect,
  loading = false,
}: {
  agents: AgentRollup[];
  /** agent_id → registry description, joined by the caller (rollups don't
   *  carry descriptions). Absent entries render an empty description cell. */
  descriptions?: Record<string, string | null | undefined>;
  /** agent_id → true when the agent's `used` latch flipped recently enough that
   *  its rollup row may not exist yet. Resolved by the caller, which also owns
   *  the "nothing is narrowing the window" half of that judgement. */
  recentlyUsed?: Record<string, boolean | undefined>;
  /** agent_id → count of published insights, joined by the caller. Absent or
   *  zero renders no pill. */
  recommendations?: Record<string, number | undefined>;
  onSelect?: (agent: AgentRollup) => void;
  /** True before the first rollups load — show a skeleton row set. */
  loading?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // `(untagged)` is the synthetic bucket /agents emits for traffic that carried
  // no x-agent-id (OPSS-352): real spend, but not a connected agent. It is not
  // counted in the header ("(3)" over two agents is a fabricated number), sorts
  // last so it can never push a real agent below the collapsed fold, and is
  // rendered inert below — its "agent page" would offer to register `(untagged)`
  // as a real agent, writing a registry row and a config profile on one click.
  const connected = agents.filter((a) => a.agent_id !== UNTAGGED_AGENT);
  const ordered = [
    ...connected,
    ...agents.filter((a) => a.agent_id === UNTAGGED_AGENT),
  ];
  const rows = expanded ? ordered : ordered.slice(0, COLLAPSED_ROWS);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <p
          className="cursor-default text-[15px] leading-none"
          style={{ color: "var(--text)" }}
        >
          Your connected agents{" "}
          <span style={{ color: "var(--text-muted)" }}>
            ({connected.length})
          </span>
        </p>
      </div>
      {loading && agents.length === 0 ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[61px] animate-pulse rounded-[5px] bg-[var(--surface-2)]"
            />
          ))}
        </div>
      ) : (
        // A zero-agent workspace renders just the quiet "(0)" header — the
        // page-level empty overlay (WorkspaceHome) owns the connect CTA.
        <div
          className="flex flex-col gap-2 overflow-y-auto"
          style={
            expanded && ordered.length > VISIBLE_ROWS
              ? { maxHeight: VISIBLE_ROWS * ROW_PX }
              : undefined
          }
        >
          {rows.map((a, i) => (
            <AgentRow
              key={a.agent_id}
              agent={a}
              description={descriptions?.[a.agent_id] ?? null}
              recentlyUsed={recentlyUsed?.[a.agent_id] === true}
              recommendations={recommendations?.[a.agent_id] ?? 0}
              synthetic={a.agent_id === UNTAGGED_AGENT}
              onSelect={onSelect}
              // Only the initially visible rows stagger in; rows revealed by
              // "Show all" appear instantly.
              floatDelay={i < COLLAPSED_ROWS ? 0.15 + i * 0.05 : null}
            />
          ))}
        </div>
      )}

      {/* Under the list, centred, per the design. It sits closer to the rows it
          belongs to than to the next section, which is the whole job of the
          asymmetric margins — the section rhythm below it (mt-5 on the page)
          supplies the clearance. */}
      {ordered.length > COLLAPSED_ROWS && (
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="flex items-center gap-1 text-[13px] transition-colors hover:text-[var(--text)]"
            style={{ color: "var(--text-muted)" }}
          >
            {/* "all", not "more": expanding reveals every agent, not a further
                page of them. */}
            {expanded ? "Show less" : "Show all"}
            <ChevronRight
              size={13}
              className={`transition-transform ${
                expanded ? "-rotate-90" : "rotate-90"
              }`}
            />
          </button>
        </div>
      )}
    </section>
  );
}

function AgentRow({
  agent: a,
  description,
  recentlyUsed,
  recommendations = 0,
  synthetic = false,
  onSelect,
  floatDelay,
}: {
  agent: AgentRollup;
  description: string | null;
  /** True when this agent was first used recently enough that its rollup row
   *  may not exist yet. */
  recentlyUsed: boolean;
  /** Published insights waiting on this agent; 0 renders no pill. */
  recommendations?: number;
  /** True for the `(untagged)` traffic bucket — a real spend row with no agent
   *  behind it, so it renders inert (no click target, no chevron). */
  synthetic?: boolean;
  onSelect?: (agent: AgentRollup) => void;
  /** Entrance stagger (seconds), or null to render without the animation. */
  floatDelay: number | null;
}) {
  const tokens =
    a.tokens.input +
    a.tokens.output +
    a.tokens.cache_read +
    a.tokens.cache_write;
  // No traffic in the selected range. Registered agents always appear (roster
  // is registry-anchored server-side), so an idle row is expected, not an
  // error — a quiet note + neutral marker make the 0s read as "quiet", not
  // broken.
  const idle = a.call_count === 0;
  // The rollups are rebuilt every few minutes, so an idle row for an agent used
  // moments ago means its first rows are still on their way, not that nothing
  // was sent (OPSS-323).
  const priming = idle && recentlyUsed;

  return (
    <div
      onClick={synthetic ? undefined : () => onSelect?.(a)}
      className={`${floatDelay !== null ? "tw-float-up" : ""} group shrink-0 rounded-[5px] border border-[var(--border)] px-5 py-[13px] transition-colors ${
        synthetic ? "cursor-default" : "cursor-pointer hover:bg-[var(--hover)]"
      } ${ROW_GRID}`}
      style={
        floatDelay !== null
          ? ({ "--float-delay": `${floatDelay}s` } as CSSProperties)
          : undefined
      }
    >
      <AgentKindLogo
        kind={a.agent_kind}
        className="size-[33px] shrink-0 overflow-hidden rounded-[5px]"
      />
      <div className="min-w-0">
        <p
          className="truncate text-[13px] font-medium leading-tight"
          style={{ color: "var(--text)" }}
        >
          {a.name ?? a.agent_id}
        </p>
        {/* The id line would just repeat the label on the synthetic row (it has
            no registry name to differ from). */}
        {!synthetic && (
          <p
            className="mt-0.5 truncate text-[11px] leading-tight"
            style={{ color: "var(--text-muted)" }}
          >
            {a.agent_id}
          </p>
        )}
      </div>
      {/* Type — the kind's display label; unknown/empty kinds read as "Other". */}
      <p
        className="truncate text-[11px]"
        style={{ color: "var(--text-muted)" }}
      >
        {kindSpec(a.agent_kind).label}
      </p>
      <p
        className="truncate text-[11px]"
        style={{ color: "var(--text-muted)" }}
        title={synthetic ? UNTAGGED_NOTE : undefined}
      >
        {synthetic
          ? UNTAGGED_NOTE
          : (description ??
            (priming
              ? "First requests received. Usage appears within ~2 minutes."
              : idle
                ? "This agent has been super quiet. Send some requests to start surfacing savings recs."
                : ""))}
      </p>
      {a.cost.total ? (
        <MonoStat value={formatUSD(a.cost.total)} label="Spend" />
      ) : (
        <span />
      )}
      {tokens ? (
        <MonoStat value={formatCompact(tokens)} label="Tokens" />
      ) : (
        <span />
      )}
      {idle ? (
        <span
          className="tw-mono justify-self-start rounded-full border px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
          style={{
            borderColor: "var(--border-2)",
            color: "var(--text-muted)",
          }}
        >
          {priming ? "Priming" : "No traffic"}
        </span>
      ) : (
        <span
          className="tw-mono justify-self-start rounded-full px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
          style={{
            // Same fixed scheme as the Savings card: mint ground, green ink,
            // identical in both themes.
            background: "var(--savings-card-bg)",
            color: "var(--savings-fg)",
          }}
        >
          <span className="tabular-nums">
            {formatPct(savedPct(a.savings_usd, a.cost.total))}
          </span>{" "}
          saved
        </span>
      )}
      {/* Savings waiting on this agent. Without it the roster gives no sign that
          an insight exists — you had to open the agent to find out. */}
      {recommendations > 0 ? (
        <span
          className="tw-mono justify-self-start rounded-full px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
          style={{
            background: "var(--rec-pill-bg)",
            color: "var(--rec-pill-fg)",
          }}
        >
          {recommendations}{" "}
          {recommendations === 1 ? "recommendation" : "recommendations"}
        </span>
      ) : (
        <span aria-hidden />
      )}
      {/* Kept as an empty cell on the synthetic row so the grid still aligns —
          the chevron promises a page that doesn't exist. */}
      {synthetic ? (
        <span aria-hidden />
      ) : (
        <ChevronRight
          size={15}
          className="shrink-0 text-[var(--text-muted)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--text)]"
        />
      )}
    </div>
  );
}

function MonoStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span
        className="tw-mono text-[11px] font-medium leading-none tabular-nums"
        style={{ color: "var(--text)" }}
      >
        {value}
      </span>
      <span
        className="tw-mono text-[10px] uppercase leading-none tracking-[0.25px]"
        style={{ color: "var(--text-muted)" }}
      >
        {label}
      </span>
    </div>
  );
}
